import { useQuery } from '@tanstack/react-query'
import api from '../lib/api'
import { useTestDataVisibility } from '../context/TestDataVisibilityContext'

/**
 * How many arrivals nobody has looked at — still in New, landing not
 * confirmed, not routed onward. The number on the council home (#1373), which
 * links the Audit queue now that the sidebar does not (#1630): a new council
 * member was told "0 waiting on you"
 * while a landing waited, and found the queue only by trying the one nav item
 * they did not recognise.
 *
 * The server counts (`landing_unreviewed`, asked at page_size 1 for its
 * `total`), so the number is about the whole set. The key sits under
 * ['demands'] on purpose: confirming or routing a landing invalidates that
 * prefix, so the badge drops the moment the work is done.
 *
 * `focusArea` scopes it to one area; omit it to count them all (admins).
 */
export function useUnreviewedLandings(focusArea: string | null | undefined, enabled: boolean) {
  const { showTestData } = useTestDataVisibility()
  return useQuery<number>({
    queryKey: ['demands', 'unreviewed-landings', { focusArea: focusArea ?? null, showTestData }],
    queryFn: async () => {
      const res = await api.get('/demands', {
        params: {
          landing_unreviewed: 'true',
          page: 1,
          page_size: 1,
          include_test: showTestData ? 'true' : 'false',
          ...(focusArea ? { focus_area: focusArea } : {}),
        },
      })
      return res.data?.total ?? 0
    },
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}
